import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as uuid from 'uuid';

import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { NavButton } from '../layout/SectionNav.jsx';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';

import { useFaceValidator } from '../hooks/useFaceValidator.js';
import { cropToFaceCentered } from '../util/selfieCrop.js';
import uploadFile from '../util/upload.js';

const CameraError = () => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			fontSize: '18px',
			color: 'rgba(255, 0, 0, .75)',
		}}
	>
		<p role="alert" aria-live="assertive" style={{ textAlign: 'center', maxWidth: '75%' }}>
			We cannot access the camera, please check if the camera is being used elsewhere and eliminate it there. Then
			please retry here.
		</p>
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '128px' }} fill="currentColor">
			<path d="m9.56 8-2-2-4.15-4.14L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.55-.18L19.73 21l1.41-1.41-8.86-8.86zM5 16V8h1.73l8 8zm10-8v2.61l6 6V6.5l-4 4V7c0-.55-.45-1-1-1h-5.61l2 2z" />
		</svg>
	</div>
);

/**
 * SelfieCamera - selfie flow component.
 *
 * Two modes:
 * - Full flow (onComplete): preview → capture → validate + crop → result (Retry or Continue).
 * - Legacy (onCapture): preview + capture only, parent handles validation.
 */
export const SelfieCamera = ({
	cameraIncludes,
	onBackPress,
	onCapture,
	onComplete,
	uploadUrl = '/api/files',
	fileType = 'face',
	name = 'userFace',
	addPendingAction = () => {},
	removePendingAction = () => {},
	text = {},
	getTextDefaults = () => ({}),
	captureLabel = 'Take photo',
}) => {
	const isLegacyMode = onCapture != null && onComplete == null;
	const [step, setStep] = useState('preview'); // 'preview' | 'evaluating' | 'result'
	const [resultState, setResultState] = useState(null); // { accepted: boolean, message?: string }
	const [imageBlob, setImageBlob] = useState(null);
	const [imageObjectURL, setImageObjectURL] = useState(null);
	const [hasError, setHasError] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const [cameraLoading, setCameraLoading] = useState(true);
	const [cameraAttachmentFailed, setCameraAttachmentFailed] = useState(false);
	const videoRef = useRef();
	const canvasRef = useRef();
	const streamRef = useRef();
	const webcamErrorTimerRef = useRef(null);
	const uploadBlobRef = useRef(null);

	const { validateFace } = useFaceValidator();

	const t = (key) => text[key] ?? getTextDefaults('EN')[key] ?? key;

	const convertCanvasToBlob = (canvas) =>
		new Promise((resolve, reject) => {
			canvas.toBlob((blob) => (blob ? resolve(blob) : reject()), 'image/jpeg', 0.9);
		});

	const stopStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
	}, []);

	const appendWebcamToVideoElement = useCallback(
		async (node) => {
			if (!node) return;
			try {
				// Release any previous stream before acquiring a new one, otherwise
				// re-acquiring (retry, error-retry interval) orphans the old stream
				// and the camera stays on until a page reload.
				stopStream();
				const devices = await navigator.mediaDevices.enumerateDevices();
				const device = cameraIncludes
					? devices.find((d) => d.kind === 'videoinput' && d.label.includes(cameraIncludes))
					: null;
				const constraints = device
					? { audio: false, video: { deviceId: device.deviceId } }
					: { audio: false, video: { facingMode: 'user' } };
				streamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
				if (webcamErrorTimerRef.current) {
					clearInterval(webcamErrorTimerRef.current);
				}
				node.srcObject = streamRef.current;
				setCameraAttachmentFailed(false);
			} catch (error) {
				setCameraAttachmentFailed(true);
				console.error(error);
			}
			setCameraLoading(false);
		},
		[cameraIncludes, stopStream],
	);

	// Stop the camera once, on unmount. Decoupled from imageObjectURL so it can
	// never be skipped (e.g. retry sets the same null URL, firing no cleanup).
	useEffect(() => stopStream, [stopStream]);

	// Revoke the preview object URL when it changes or on unmount.
	useEffect(() => {
		if (!imageObjectURL) return undefined;
		return () => URL.revokeObjectURL(imageObjectURL);
	}, [imageObjectURL]);

	useEffect(() => {
		if (videoRef.current && cameraAttachmentFailed && webcamErrorTimerRef.current === null) {
			webcamErrorTimerRef.current = setInterval(() => {
				appendWebcamToVideoElement(videoRef.current);
			}, 3000);
		}
		return () => {
			if (webcamErrorTimerRef.current) {
				clearInterval(webcamErrorTimerRef.current);
			}
		};
	}, [cameraAttachmentFailed, appendWebcamToVideoElement]);

	const handleVideoRef = useCallback(
		(node) => {
			videoRef.current = node;
			if (node) appendWebcamToVideoElement(node);
		},
		[appendWebcamToVideoElement],
	);

	const captureFromVideo = useCallback(async () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas) return null;

		// Brief delay to avoid iOS Safari black-frame bug (drawImage on video can yield black)
		await new Promise((r) => setTimeout(r, 100));

		const w = video.videoWidth;
		const h = video.videoHeight;
		canvas.width = w;
		canvas.height = h;
		const ctx = canvas.getContext('2d');
		if (!ctx) return null;

		ctx.drawImage(video, 0, 0, w, h);
		const blob = await convertCanvasToBlob(canvas);
		return { blob, width: w, height: h };
	}, []);

	const handleCapture = useCallback(async () => {
		const captured = await captureFromVideo();
		if (!captured) return;

		if (isLegacyMode) {
			onCapture?.(captured);
			return;
		}

		const { blob, width: w, height: h } = captured;
		setStep('evaluating');
		setResultState(null);

		try {
			const result = await validateFace(blob, w, h);

			if (!result.accepted) {
				const enText = getTextDefaults('EN');
				const message = text[result.reason] ?? enText[result.reason] ?? result.reason;
				setResultState({ accepted: false, message });
				setStep('result');
				return;
			}

			const croppedBlob = await cropToFaceCentered(blob, result.faceBoundingBox, w, h);
			if (!croppedBlob || croppedBlob.size < 500) {
				throw new Error('Capture produced invalid image');
			}
			uploadBlobRef.current = croppedBlob;
			const objectUrl = URL.createObjectURL(croppedBlob);
			setImageObjectURL(objectUrl);
			setImageBlob(croppedBlob);
			setResultState({ accepted: true });
			setStep('result');
		} catch (err) {
			console.error(err);
			setResultState({ accepted: false, message: t('error') });
			setStep('result');
		}
	}, [isLegacyMode, onCapture, captureFromVideo, validateFace, text, getTextDefaults, t]);

	const handleRetry = useCallback(() => {
		if (imageObjectURL) URL.revokeObjectURL(imageObjectURL);
		uploadBlobRef.current = null;
		setResultState(null);
		setImageBlob(null);
		setImageObjectURL(null);
		setHasError(false);
		setStep('preview');
	}, [imageObjectURL]);

	const handleContinue = useCallback(async () => {
		const blobToUpload = uploadBlobRef.current ?? imageBlob;
		if (!blobToUpload || !onComplete) return;

		const action = uuid.v4();
		setIsUploading(true);
		try {
			addPendingAction(action);

			// Pass Blob directly (not ArrayBuffer) - better Safari/iOS compatibility
			const file = await uploadFile(uploadUrl, blobToUpload, false, {
				contentType: 'image/jpeg',
				type: fileType,
				filename: 'FaceImage.jpg',
			});

			onComplete({ target: { name, value: file } });
		} catch (err) {
			console.error(err);
			setHasError(true);
			onComplete({ target: { name, value: null } });
		} finally {
			removePendingAction(action);
			setIsUploading(false);
		}
	}, [imageBlob, onComplete, uploadUrl, name, addPendingAction, removePendingAction]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
				width: '100%',
			}}
		>
			{/* Content area */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					px: 2,
				}}
			>
				{/* Preview - camera vertically centered between top and buttons */}
				{step === 'preview' && (
					<Box
						sx={{
							width: '100%',
							maxWidth: 650,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							flex: 1,
							minHeight: 0,
						}}
					>
						<Box
							sx={{
								position: 'relative',
								width: 'min(100%, calc(100vh - 200px))',
								aspectRatio: '1',
								boxSizing: 'border-box',
								flexShrink: 0,
							}}
						>
							<Box
								sx={{
									position: 'relative',
									width: '100%',
									height: '100%',
									borderRadius: 3,
									overflow: 'hidden',
								}}
							>
								<video
									ref={handleVideoRef}
									autoPlay
									playsInline
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%',
										objectFit: 'cover',
										objectPosition: 'center',
										display: cameraLoading || cameraAttachmentFailed ? 'none' : 'block',
									}}
								/>
							</Box>
							<canvas ref={canvasRef} style={{ display: 'none' }} />
						</Box>

						{cameraLoading && <CircularProgress size={60} sx={{ mt: 2 }} />}
						{cameraAttachmentFailed && <CameraError />}
					</Box>
				)}

				{/* Evaluating */}
				{step === 'evaluating' && (
					<Box
						sx={{
							position: 'relative',
							width: '100%',
							minHeight: 300,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Backdrop
							open
							sx={{
								position: 'absolute',
								flexDirection: 'column',
								bgcolor: 'rgba(255,255,255,0.95)',
								zIndex: 1300,
							}}
						>
							<CircularProgress size={60} />
							<Typography variant="h6" sx={{ mt: 2 }}>
								{t('evaluating')}
							</Typography>
						</Backdrop>
					</Box>
				)}

				{/* Result */}
				{step === 'result' && resultState && (
					<>
						{resultState.accepted ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
									maxWidth: 650,
									padding: 2,
									gap: 2,
								}}
							>
								<CheckCircleOutlineRoundedIcon sx={{ fontSize: 48, color: 'success.main' }} aria-hidden />
								<Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center' }}>
									{t('successMessage')}
								</Typography>
								<img
									src={imageObjectURL}
									alt="Your selfie"
									style={{ borderRadius: 12, objectFit: 'contain', maxWidth: '100%', maxHeight: '50vh' }}
								/>
							</Box>
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
									maxWidth: 650,
									padding: 2,
									gap: 2,
								}}
							>
								<WarningAmberRoundedIcon sx={{ fontSize: 64, color: 'warning.main' }} aria-hidden />
								<Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 500, px: 2 }}>
									{resultState.message || t('retryMessage')}
								</Typography>
								<Button
									variant="outlined"
									color="primary"
									size="large"
									startIcon={<CameraAltOutlinedIcon />}
									onClick={handleRetry}
									sx={{ mt: 1 }}
								>
									{t('takeNewPicture') || t('retry')}
								</Button>
							</Box>
						)}
						{hasError && (
							<Alert variant="filled" severity="error" sx={{ mt: 2, maxWidth: 650, width: '100%' }}>
								{t('error')}
							</Alert>
						)}
					</>
				)}
			</Box>

			{/* Bottom buttons - SectionNav style */}
			{step === 'preview' && !cameraLoading && !cameraAttachmentFailed && (
				<Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', width: '100%', px: 2, pt: 2, pb: 2 }}>
					<ButtonGroup sx={{ maxWidth: 650, width: '100%' }}>
						<NavButton onClick={onBackPress} beforeIcon={<ArrowBackIcon sx={{ flexGrow: 0, marginRight: 1 }} />}>
							{t('back') || 'Back'}
						</NavButton>
						<NavButton onClick={handleCapture} color="secondary" afterIcon={<ArrowForwardIcon />}>
							{isLegacyMode ? captureLabel : t('capture') || t('getStarted')}
						</NavButton>
					</ButtonGroup>
				</Box>
			)}

			{step === 'result' && resultState && resultState.accepted && (
				<Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', width: '100%', px: 2, pt: 2, pb: 4 }}>
					<ButtonGroup sx={{ maxWidth: 650, width: '100%' }}>
						<NavButton onClick={handleRetry} beforeIcon={<ArrowBackIcon sx={{ flexGrow: 0, marginRight: 1 }} />}>
							{t('retry')}
						</NavButton>
						<NavButton
							onClick={handleContinue}
							color="secondary"
							afterIcon={<ArrowForwardIcon />}
							disabled={hasError || isUploading}
						>
							{t('confirm')}
						</NavButton>
					</ButtonGroup>
				</Box>
			)}
		</Box>
	);
};

SelfieCamera.displayName = 'SelfieCamera';

export default SelfieCamera;
